import { useAuth } from '@backrs_ui/grow-ui/src/hooks/useAuth';
import { useGetMostRecentPlanQuery } from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.api';
import { MostRecentSubmission } from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.types';

type BoosterPlanState = Readonly<{
  mostRecent: MostRecentSubmission | undefined;
  isPending: boolean;
  loadFailed: boolean;
  hasPlan: boolean;
  refetch: () => void;
}>;

/**
 * The caller's most recent finished submission, plus the load states a view of it branches on.
 * `isPending` covers a refetch that has nothing to show yet, so a decision is never made on data
 * about to arrive — but a refetch over data already held is not pending, or the day-completion and
 * quiz mutations that invalidate this read would blank the plan on every toggle. A 404 means "no
 * finished plan", not a failure; only a genuinely broken read with nothing cached sets `loadFailed`.
 */
export const useBoosterPlan = (): BoosterPlanState => {
  const { isUserOrGuest } = useAuth();

  const { data, isLoading, isFetching, isError, error, refetch } = useGetMostRecentPlanQuery(
    undefined,
    { skip: !isUserOrGuest },
  );

  return {
    mostRecent: data,
    isPending: isLoading || (isFetching && data === undefined),
    loadFailed: isError && data === undefined && (error as { status?: number })?.status !== 404,
    hasPlan: data?.completedAt != null,
    refetch,
  };
};
